import { supabase } from '../client'

/* ── REALTIME SUBSCRIPTIONS ─────────────────────────────────────────── */

/**
 * Die Tabellen, die **zurufen** dürfen — alle, die synchronisiert werden.
 *
 * Muss zu den Pull-Durchgängen in `sync-engine.performSync` passen: Wer
 * heruntergeladen wird, muss auch melden können, dass es etwas zu holen gibt.
 * Jede hier gelistete Tabelle braucht einen Eintrag in der
 * `supabase_realtime`-Publication — sonst meldet Supabase brav `SUBSCRIBED`,
 * und es kommt nur nie ein Ereignis (siehe Migration
 * `2026…_enable_realtime_all_synced_tables.sql`).
 */
export const REALTIME_SYNCED_TABLES = [
  'tasks',
  'subtasks',
  'projects',
  'sections',
  'tags',
  'canvas_notes',
  'goals',
  'habits',
  'habit_logs',
  'reward_usages',
  'user_preferences',
  'time_entries',
  'task_comments',
  'project_comments',
  'task_reminders',
] as const

/**
 * **Der Zuruf** — Supabase meldet, dass ein ANDERES Gerät etwas geändert hat.
 *
 * Ein Kanal für alle Tabellen, nicht einer je Tabelle (ADR 0017). Der Rückruf
 * ist derselbe für alle: Ein Zuruf trägt **keine Nutzlast**, er sagt „schau
 * nach", und die Antwort ist immer ein ganz normaler Sync über alle Tabellen.
 * Welche Tabelle gerufen hat, spielt also keine Rolle — und darum gibt es auch
 * nichts zu unterscheiden.
 *
 * Vorher waren es drei Kanäle für drei Tabellen, mit drei identischen
 * Rückrufen. Ein neues Entity brauchte dann einen eigenen Kanal, den niemand
 * vermisst, wenn er fehlt: Genau so blieben `canvas_notes` von August bis
 * September 2026 still.
 *
 * `onStatus` bekommt den Status dieses einen Kanals — daran hängt
 * `isRealtimeConnected` im `SyncProvider` und damit der Aufhol-Sync nach einer
 * Unterbrechung.
 *
 * Wird der **Projekt-Canvas** später zu zweit bedienbar, tritt dort etwas
 * anderes an die Stelle des Zurufs: ein Rückruf, der `payload.new` direkt
 * schreibt, statt einen vollen Sync anzustoßen. Derselbe Kanal, anderer
 * Rückruf — die Bindung unten bliebe stehen.
 */
export function subscribeToSyncedTables(
  userId: string,
  callback: () => void,
  onStatus?: (status: string) => void
): () => void {
  if (!supabase) return () => {}

  let channel = supabase.channel(`sync_${userId}`)

  for (const table of REALTIME_SYNCED_TABLES) {
    channel = channel.on(
      'postgres_changes',
      { event: '*', schema: 'public', table, filter: `user_id=eq.${userId}` },
      () => callback()
    )
  }

  const subscribed = channel.subscribe((status, err) => {
    if (err) console.warn('[Realtime] Sync channel error:', err.message)
    onStatus?.(status)
  })

  return () => { supabase!.removeChannel(subscribed) }
}
